import React, { useEffect, useRef, useState, useCallback } from "react";
import { PlusIcon, MinusIcon } from "@radix-ui/react-icons";
import { Editor } from "@tldraw/tldraw";
import { makeStyles } from "@material-ui/core";
import Popover from "@material-ui/core/Popover";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { DropdownChevronIcon } from "./Primitives/icons/DropdownChevronIcon";
import SideBarSlider from "./SidebarSlider";
import ColorPickerPopover from "./ColorPickerPopover";
import { ToggleEyesIcon } from "./SidebarBottomTool";
import OutlineSidebar from "./OutlineSidebar";
import ShadowItem from "./ShadowItem";
import { BUILDER_COLORS } from "../../constants";
import { hideSelectionTemporarily } from "../../utils";
import { createDefaultShadowV2, normalizeShadow } from "../state/newShapes/shadowUtils";
import { useDragToAdjust } from "../hooks/useDragToAdjust";

// Cap the per-shape rendering inside the right sidebar. Above this size the
// fill/effects sections each render N × (ColorPickerPopover | ShadowItem |
// inputs) — at 2000 selected shapes that's 5–10k MUI components, blocking
// the main thread for several seconds. Matches the StyleMenu bailout: bulk
// selections (>30) are for block actions (move, duplicate, delete), not
// per-shape edits. Keep this in sync with StyleMenu's threshold.
const BULK_SELECTION_RENDER_THRESHOLD = 30;

type ShadowType = {
  id: string;
  dx: number;
  dy: number;
  shadowBlur: number;
  color: string;
  opacity: number;
  isHidden: boolean;
};

interface EffectSidebarProps {
  editor: Editor;
  selectedShape: any;
  setSelectedShape: (shape: any) => void;
  prop: string;
  handleSliderChange: (key: string, value: number) => void;
  selectedShapes: any[];
}

const useStyles = makeStyles({
  inputRoot: {
    "& .MuiOutlinedInput-root": {
      height: "32px",
      padding: "0",
      display: "flex",
      justifyContent: "center",
      border: "1px solid #E2E2FC",
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
      "& fieldset": {
        borderColor: "#E2E2FC",
      },
      "&:hover fieldset": {
        borderColor: "#E2E2FC",
      },
      "& .MuiAutocomplete-input": {
        padding: "0px !important",
      },
    },

    "& .MuiAutocomplete-clearIndicator": {
      display: "none !important",
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      paddingRight: "0 !important",
    },
  },
  selectRoot: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      fontSize: "14px",
      color: "#00112B",
      "& fieldset": {
        borderColor: "#E2E2FC",
      },
      "&:hover fieldset": {
        borderColor: "#E2E2FC",
      },
    },
  },
  listPadding: {
    padding: "0px !important",
  },
  menuPaper: {
    borderRadius: "4px !important",
    "& .MuiMenuItem-root": {
      position: "relative",
      paddingRight: "32px",
      "&:hover": {
        backgroundColor: "#E2E2FC",
      },
      "&.Mui-selected": {
        backgroundColor: "#FFFFFF !important",
        border: "1px solid #2E3860",
        borderRadius: "4px",
        "&::after": {
          content: '""',
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          width: "12px",
          height: "12px",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path fill-rule='evenodd' clip-rule='evenodd' d='M3.54 4.86L2.7 5.7L5.4 8.4L11.4 2.4L10.56 1.56L5.4 6.72L3.54 4.86ZM10.8 6C10.8 8.64 8.64 10.8 6 10.8C3.36 10.8 1.2 8.64 1.2 6C1.2 3.36 3.36 1.2 6 1.2C6.48 1.2 6.9 1.26 7.32 1.38L8.28 0.42C7.56 0.18 6.78 0 6 0C2.7 0 0 2.7 0 6C0 9.3 2.7 12 6 12C9.3 12 12 9.3 12 6H10.8Z' fill='%2357578E'/></svg>\")",
        },
      },
      "&.Mui-selected:hover": {
        backgroundColor: "#FFFFFF !important",
      },
    },
  },
  paper: {
    borderRadius: "4px !important",
    padding: "0",
    scale: 1.5,
    display: "flex",
    justifyContent: "center",
    border: "1px solid #E2E2FC",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    transform: "translateX(-150px) translateY(18px) !important",
  },
  menuItem: {
    "&:hover": {
      backgroundColor: "#E2E2FC",
    },
  },
});

// Module-scope helper: no closure deps, so keeping it here avoids re-allocating
// it on every render of the component.
const isQrCodeShape = (shape: any): boolean => shape?.type === "customImage" && shape?.props?.qrCodeLink !== undefined;

const EffectSidebar = ({
  editor,
  selectedShape,
  setSelectedShape,
  prop,
  handleSliderChange,
  selectedShapes,
}: EffectSidebarProps) => {
  const [anchorElOutShadow, setAnchorElOutShadow] = useState<Record<string, HTMLElement | null>>({});
  const [newShadowId, setNewShadowId] = useState<string | null>(null);
  const [position, setPosition] = useState<Record<string, { x: number; y: number }>>({});
  const [isDragging, setIsDragging] = useState<string | false>(false);
  const isInnerShadowDisabled = false;

  const containerRefOut = useRef<HTMLDivElement>(null);
  const containerRefIn = useRef<HTMLDivElement>(null);

  const classes = useStyles();

  useEffect(() => {
    if (selectedShape?.props?.outShadowArray && selectedShape?.props?.inShadowArray) {
      type PositionMap = Record<string, { x: number; y: number }>;
      const collect = (raws: any[], type: "drop" | "inner"): PositionMap =>
        raws.reduce<PositionMap>((acc, raw) => {
          const s = normalizeShadow(raw, type);
          acc[s.id] = { x: s.x, y: s.y };
          return acc;
        }, {});

      const initialPosition: PositionMap = {
        ...collect(Object.values(selectedShape.props.outShadowArray), "drop"),
        ...collect(Object.values(selectedShape.props.inShadowArray), "inner"),
      };
      setPosition(initialPosition);
    }
  }, [selectedShape?.props?.outShadowArray, selectedShape?.props?.inShadowArray]);

  useEffect(() => {
    if (newShadowId) {
      setNewShadowId(null);
    }
  }, [newShadowId]);

  const handleMouseMove = (e: MouseEvent, shadowId: string, type: "outShadow" | "inShadow") => {
    const containerRef = type === "outShadow" ? containerRefOut : containerRefIn;
    if (isDragging && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const newX = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
      const newY = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);

      const mappedX = Math.round((newX / rect.width) * 20 - 10);
      const mappedY = Math.round((newY / rect.height) * 20 - 10);

      setPosition((prevPosition) => ({
        ...prevPosition,
        [shadowId]: { x: mappedX, y: mappedY },
      }));

      updateShapes((shape) => {
        const updatedShadows = {
          ...shape.props[`${type}Array`],
          [shadowId]: { ...shape.props[`${type}Array`][shadowId], dx: mappedX, dy: mappedY },
        };

        return {
          ...shape,
          props: {
            ...shape.props,
            [`${type}Array`]: updatedShadows,
          },
        };
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    window.removeEventListener("mousemove", handleMouseMoveWrapper);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMoveWrapper = (e: MouseEvent) => {
    if (isDragging) {
      const [type, shadowId] = isDragging.split("-");
      handleMouseMove(e, shadowId, type as "outShadow" | "inShadow");
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMoveWrapper);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMoveWrapper);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleMouseDown = (e: React.MouseEvent, shadowId: string, type: "outShadow" | "inShadow") => {
    setIsDragging(`${type}-${shadowId}`);
  };

  const handleClosePopover = (shadowId: string) => {
    setAnchorElOutShadow((prev) => ({ ...prev, [shadowId]: null }));
  };

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLElement>, shapeId: string) => {
    // For now, just store the shapeId - we'll handle popover management differently
    // since we're now showing individual popovers per shape
    setAnchorElOutShadow((prev) => ({
      ...prev,
      [`${shapeId}`]: event.currentTarget as HTMLElement,
    }));
  }, []);

  const handleCreateDefaultColor = () => {
    const newColorKey = Date.now().toString();
    const newColor = BUILDER_COLORS.NEW_COLOR;

    updateShapes((shape) => {
      const updatedProps = { ...shape.props };
      updatedProps.colorMix = { ...updatedProps.colorMix, [newColorKey]: newColor };
      return {
        ...shape,
        props: updatedProps,
      };
    });
  };

  const handleCreateDefaultColorForShape = (shape: any) => {
    const newColorKey = Date.now().toString();
    const newColor = BUILDER_COLORS.NEW_COLOR;

    const updatedProps = { ...shape.props };
    updatedProps.colorMix = { ...updatedProps.colorMix, [newColorKey]: newColor };

    const updatedShape = {
      ...shape,
      props: updatedProps,
    };

    editor.updateShape(updatedShape);

    if (selectedShape && selectedShape.id === shape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleCreateDefaultOutShadow = (createShadow?: string, shapeId?: string) => {
    const newShadow = createDefaultShadowV2("drop");
    const newShadowId = newShadow.id;

    if (shapeId) {
      // Work with specific shape
      const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
      if (!targetShape) return;

      const updatedProps = { ...targetShape.props };
      updatedProps.outShadowArray = { ...updatedProps.outShadowArray, [newShadowId]: newShadow };
      if (!createShadow) {
        updatedProps.isCreatedFilterBlur = false;
        updatedProps.filterBlur = 0;
        updatedProps.filterBlurValueWhenHidden = 0;
      }

      const updatedShape = {
        ...targetShape,
        props: updatedProps,
      };

      editor.updateShape(updatedShape);

      if (selectedShape && selectedShape.id === targetShape.id) {
        setSelectedShape(updatedShape);
      }

      hideSelectionTemporarily(updatedShape, editor);
    } else {
      // Work with all shapes (original behavior)
      updateShapes((shape) => {
        const updatedProps = { ...shape.props };
        updatedProps.outShadowArray = { ...updatedProps.outShadowArray, [newShadowId]: newShadow };
        if (!createShadow) {
          updatedProps.isCreatedFilterBlur = false;
          updatedProps.filterBlur = 0;
          updatedProps.filterBlurValueWhenHidden = 0;
        }
        return {
          ...shape,
          props: updatedProps,
        };
      });
    }
  };

  const handleCreateDefaultInShadow = (shapeId?: string) => {
    const newShadow = createDefaultShadowV2("inner");
    const newShadowId = newShadow.id;

    updateShapes((shape) => {
      const updatedProps = { ...shape.props };
      updatedProps.inShadowArray = { ...updatedProps.inShadowArray, [newShadowId]: newShadow };
      updatedProps.isCreatedFilterBlur = false;
      updatedProps.filterBlur = 0;
      updatedProps.filterBlurValueWhenHidden = 0;
      return {
        ...shape,
        props: updatedProps,
      };
    });
  };

  // to update the shadow blur value of ShadowArray
  const handleSliderChangeShadowBlurForShapes = (key: string, value: number, shapeId: string) => {
    // Only applies to the specific shape that contains this shadow
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    const [_, shadowId, propKey] = key.split(".");

    // Determine the type based on where the shadow exists
    const type = targetShape.props.outShadowArray[shadowId] ? "outShadow" : "inShadow";

    const updatedShadows = {
      ...targetShape.props[`${type}Array`],
      [shadowId]: {
        ...targetShape.props[`${type}Array`][shadowId],
        [propKey]: value,
      },
    };

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        [`${type}Array`]: updatedShadows,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  // to show or hide the shadow
  const handleShowToggleForShapes = (shadowId: string, shapeId: string) => {
    // Only applies to the specific shape that contains this shadow
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    // Determine the type based on where the shadow exists
    const type = targetShape.props.outShadowArray[shadowId] ? "outShadow" : "inShadow";

    if (!targetShape.props[`${type}Array`] || !targetShape.props[`${type}Array`][shadowId]) {
      return;
    }

    const updatedShadows = {
      ...targetShape.props[`${type}Array`],
      [shadowId]: {
        ...targetShape.props[`${type}Array`][shadowId],
        isHidden: !targetShape.props[`${type}Array`][shadowId].isHidden,
      },
    };

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        [`${type}Array`]: updatedShadows,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  // remove the shadow from the selected shape
  const handleRemoveShadowForShapes = (shadowId: string, shapeId: string) => {
    // Only applies to the specific shape that contains this shadow
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    // Determine the type based on where the shadow exists
    const type = targetShape.props.outShadowArray[shadowId] ? "outShadow" : "inShadow";

    const updatedShadows = { ...targetShape.props[`${type}Array`] };
    delete updatedShadows[shadowId];

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        [`${type}Array`]: updatedShadows,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleToggleLayerBlur = () => {
    const { isHiddenLayerBlur } = selectedShape.props;

    const updatedShape = {
      ...selectedShape,
      props: {
        ...selectedShape.props,
        isHiddenLayerBlur: !isHiddenLayerBlur,
      },
    };

    editor.updateShape(updatedShape);

    setSelectedShape(updatedShape);

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleToggleLayerBlurForShape = (shapeId: string) => {
    // Only applies to the specific shape that contains this layer blur
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    const { isHiddenLayerBlur } = targetShape.props;

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        isHiddenLayerBlur: !isHiddenLayerBlur,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleRemoveLayerBlur = (callback?: () => void) => {
    const updatedShape = {
      ...selectedShape,
      props: {
        ...selectedShape.props,
        isCreatedFilterBlur: false,
        filterBlur: 0,
        filterBlurValueWhenHidden: 0,
      },
    };

    editor.updateShape(updatedShape);
    setSelectedShape(updatedShape);

    hideSelectionTemporarily(updatedShape, editor);

    if (callback) {
      callback();
    }
  };

  const handleFilterBlurChangeForShape = (shapeId: string, value: number) => {
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;
    const clamped = Math.max(0, Math.min(50, value));
    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        filterBlur: clamped,
        isHiddenLayerBlur: false,
      },
    };
    editor.updateShape(updatedShape);
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }
  };

  const handleRemoveLayerBlurForShape = (shapeId: string) => {
    // Only applies to the specific shape that contains this layer blur
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape) return;

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        isCreatedFilterBlur: false,
        filterBlur: 0,
        filterBlurValueWhenHidden: 0,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleRemoveColor = (key: string) => {
    const updatedColorMix = { ...selectedShape.props.colorMix };
    delete updatedColorMix[key];

    const updatedShape = {
      ...selectedShape,
      props: {
        ...selectedShape.props,
        colorMix: updatedColorMix,
      },
    };

    editor.updateShape(updatedShape);
    setSelectedShape(updatedShape);

    hideSelectionTemporarily(updatedShape, editor);
  };

  const handleRemoveColorForShape = (shape: any, key: string) => {
    const updatedColorMix = { ...shape.props.colorMix };
    delete updatedColorMix[key];

    const updatedShape = {
      ...shape,
      props: {
        ...shape.props,
        colorMix: updatedColorMix,
      },
    };

    editor.updateShape(updatedShape);

    if (selectedShape && selectedShape.id === shape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);

    // Force a re-render to ensure the UI reflects the changes immediately
    const currentSelectedIds = editor.getSelectedShapeIds();
    if (currentSelectedIds.length > 0) {
      editor.setSelectedShapes(currentSelectedIds);
    }
  };

  const updateShapes = (updateFn: (shape: any) => any) => {
    selectedShapes.forEach((shape) => {
      const updatedShape = updateFn(shape);
      editor.updateShape(updatedShape);

      if (selectedShape && selectedShape.id === shape.id) {
        setSelectedShape(updatedShape);
      }

      hideSelectionTemporarily(updatedShape, editor);
    });
  };

  const handleToggleColorVisibilityForShapes = (key: string, shapeId: string) => {
    // Only applies to the specific shape that contains this color
    const targetShape = selectedShapes.find((shape) => shape.id === shapeId);
    if (!targetShape || !targetShape.props.colorMix || !targetShape.props.colorMix[key]) {
      return;
    }

    const shouldHide = !targetShape.props.colorMix[key].isHidden;
    const updatedColorMix = {
      ...targetShape.props.colorMix,
      [key]: {
        ...targetShape.props.colorMix[key],
        isHidden: shouldHide,
      },
    };

    const visibleColors = Object.values(updatedColorMix).filter((c: any) => !c.isHidden);
    const fill = visibleColors.length > 0 ? targetShape.props.fill : "transparent";

    const updatedShape = {
      ...targetShape,
      props: {
        ...targetShape.props,
        colorMix: updatedColorMix,
        fill,
      },
    };

    editor.updateShape(updatedShape);

    // Only update selectedShape if it's the specific shape that was modified
    if (selectedShape && selectedShape.id === targetShape.id) {
      setSelectedShape(updatedShape);
    }

    hideSelectionTemporarily(updatedShape, editor);

    // Force re-render to reflect changes immediately
    const currentSelectedIds = editor.getSelectedShapeIds();
    if (currentSelectedIds.length > 0) {
      editor.setSelectedShapes(currentSelectedIds);
    }
  };

  const combinedShadows = selectedShape
    ? [
        ...(Object.values(selectedShape.props.outShadowArray || {}) as ShadowType[]),
        ...(Object.values(selectedShape.props.inShadowArray || {}) as ShadowType[]),
      ].sort((a, b) => a.id.localeCompare(b.id))
    : [];

  const hasFillContent = selectedShapes.some(
    (shape) => shape?.props?.colorMix && Object.keys(shape.props.colorMix).length > 0
  );
  const hasEffectsContent = selectedShapes.some(
    (shape) =>
      shape?.props?.isCreatedFilterBlur ||
      Object.keys(shape?.props?.outShadowArray || {}).length > 0 ||
      Object.keys(shape?.props?.inShadowArray || {}).length > 0
  );

  // QR codes (customImage with qrCodeLink) manage their colors via the QR-specific
  // panel, so the generic Fill section is hidden when every selected shape is a QR.
  const selectionIsOnlyQrCodes = selectedShapes.length > 0 && selectedShapes.every(isQrCodeShape);

  return (
    <div className="flex flex-col gap-2 w-full">
      {selectedShapes.length > BULK_SELECTION_RENDER_THRESHOLD ? (
        <div className="text-xs text-[#57578E] px-4 py-2">
          Select up to {BULK_SELECTION_RENDER_THRESHOLD} shapes to edit their <strong>fill</strong>,{" "}
          <strong>stroke</strong> and <strong>effects</strong>.
        </div>
      ) : (
        <>
          {!selectionIsOnlyQrCodes && (
            <div
              style={{ paddingTop: hasFillContent ? 0 : 6, paddingBottom: hasFillContent ? 0 : 6 }}
              className={hasFillContent ? "flex flex-col gap-2" : undefined}
            >
              <div className="flex items-center justify-between px-4 gap-2">
                {/* Same gate as the PlusIcon — title is clickable only
                when the icon would have been visible. Avoids
                "ghost clicks" on a label that looks interactive but
                has no available action. */}
                <Title name={"Fill"} onClick={selectedShapes.length === 1 ? handleCreateDefaultColor : undefined} />
                {selectedShapes.length === 1 && (
                  <PlusIcon className="h-5 w-5 cursor-pointer" onClick={handleCreateDefaultColor} />
                )}
              </div>
              <div className="flex items-center justify-between px-4">
                {selectedShapes.length > 0 && selectedShapes.length <= BULK_SELECTION_RENDER_THRESHOLD && (
                  <div className="flex flex-col gap-2 w-full">
                    {selectedShapes
                      .filter((shape) => shape && shape.id)
                      .map((shape, shapeIndex) => (
                        <div key={shape.id} className="w-full">
                          {shape.props?.colorMix && Object.keys(shape.props.colorMix).length > 0 && (
                            <div className="flex flex-col gap-2">
                              {Object.entries(shape.props.colorMix).map(([key, colorObj]) => (
                                <div key={`${shape.id}-${key}`} className="w-full flex items-center justify-between">
                                  <ColorPickerPopover
                                    colorProp={`colorMix.${key}`}
                                    selectedShape={shape}
                                    setSelectedShape={setSelectedShape}
                                    editor={editor}
                                    color={(colorObj as { color: string }).color}
                                    selectedShapes={[shape]}
                                  />
                                  <div className="flex items-center gap-1">
                                    <ToggleEyesIcon
                                      isOpen={!(colorObj as { isHidden: boolean }).isHidden}
                                      onClick={() => handleToggleColorVisibilityForShapes(key, shape.id)}
                                    />
                                    {selectedShapes.length === 1 && (
                                      <MinusIcon
                                        className="h-5 w-5 cursor-pointer"
                                        onClick={() => handleRemoveColorForShape(shape, key)}
                                      />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Divider after Fill — hidden when only QR codes are selected (no Fill above) */}
          {!selectionIsOnlyQrCodes && (
            <div
              style={{
                paddingLeft: "8px",
                paddingRight: "8px",
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "#E2E2FC",
                }}
              />
            </div>
          )}{" "}
          {/* Show OutlineSidebar for all shapes except drawings (they have their own weight control) and QR codes */}
          {selectedShape?.type !== "customDraw" &&
            selectedShape?.type !== "customHighlight" &&
            !selectionIsOnlyQrCodes && (
              <OutlineSidebar
                selectedShape={selectedShape}
                editor={editor}
                handleSliderChange={handleSliderChange}
                setSelectedShape={setSelectedShape}
                selectedShapes={selectedShapes}
              />
            )}
          {!selectionIsOnlyQrCodes && (
            <div
              style={{
                paddingLeft: "8px",
                paddingRight: "8px",
                width: "100%",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: "#E2E2FC",
                }}
              />
            </div>
          )}
          {/* Hide effects section for drawing shapes */}
          {selectedShape?.type !== "customHighlight" &&
            selectedShape?.type !== "customDraw" &&
            selectedShapes.some(
              (shape) => shape && shape.type !== "customHighlight" && shape.type !== "customDraw"
            ) && (
              <div
                data-effects-section=""
                style={{ paddingTop: hasEffectsContent ? 0 : 6, paddingBottom: hasEffectsContent ? 0 : 6 }}
                className={hasEffectsContent ? "flex flex-col gap-2" : undefined}
              >
                <div className="flex items-center justify-between px-4 gap-2">
                  <Title
                    name={"Effects"}
                    onClick={
                      selectedShapes.length === 1 || !hasEffectsContent
                        ? () => handleCreateDefaultOutShadow("createShadow")
                        : undefined
                    }
                  />
                  {(selectedShapes.length === 1 || !hasEffectsContent) && (
                    <PlusIcon
                      className="h-5 w-5 cursor-pointer"
                      onClick={() => handleCreateDefaultOutShadow("createShadow")}
                    />
                  )}
                </div>
                <div className="relative flex flex-col gap-2 w-full px-4">
                  {selectedShapes.length <= BULK_SELECTION_RENDER_THRESHOLD &&
                    selectedShapes
                      .filter((shape) => shape && shape.id)
                      .map((shape, shapeIndex) => (
                        <div key={shape.id} className="w-full">
                          {/* Individual effects for this shape */}
                          {(shape.props?.isCreatedFilterBlur ||
                            Object.keys(shape.props?.outShadowArray || {}).length > 0 ||
                            Object.keys(shape.props?.inShadowArray || {}).length > 0) && (
                            <div className="flex flex-col gap-2">
                              {/* Show shadows for this shape */}
                              {[
                                ...Object.values(shape.props?.outShadowArray || {}),
                                ...Object.values(shape.props?.inShadowArray || {}),
                              ].map((shadow: any) => (
                                <div key={`${shape.id}-${shadow.id}`} className="w-full flex items-center">
                                  <ShadowItem
                                    shadow={shadow}
                                    type={shape.props.outShadowArray[shadow.id] ? "outShadow" : "inShadow"}
                                    selectedShape={shape}
                                    setSelectedShape={setSelectedShape}
                                    editor={editor}
                                    handleOpenPopover={(event) => handleOpenPopover(event, shape.id)}
                                    handleRemoveShadow={(shadowId) => handleRemoveShadowForShapes(shadowId, shape.id)}
                                    handleShowToggle={(shadowId) => handleShowToggleForShapes(shadowId, shape.id)}
                                    handleSliderChangeShadowBlur={(shadowId, value) =>
                                      handleSliderChangeShadowBlurForShapes(shadowId, value, shape.id)
                                    }
                                    position={position}
                                    setPosition={setPosition}
                                    handleMouseDown={handleMouseDown}
                                    containerRef={
                                      shape.props.outShadowArray[shadow.id] ? containerRefOut : containerRefIn
                                    }
                                    anchorElOutShadow={anchorElOutShadow}
                                    handleClosePopover={handleClosePopover}
                                  />
                                  <div className="flex items-center gap-1 ml-auto">
                                    <ToggleEyesIcon
                                      isOpen={!shadow.isHidden}
                                      onClick={() => handleShowToggleForShapes(shadow.id, shape.id)}
                                    />
                                    {selectedShapes.length === 1 && (
                                      <MinusIcon
                                        className="h-5 w-5 cursor-pointer"
                                        onClick={() => handleRemoveShadowForShapes(shadow.id, shape.id)}
                                      />
                                    )}
                                  </div>
                                </div>
                              ))}

                              {/* Show layer blur for this shape */}
                              {shape.props?.isCreatedFilterBlur && (
                                <div className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <LayerBlurRow
                                      shape={shape}
                                      onChange={(value) => handleFilterBlurChangeForShape(shape.id, value)}
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <ToggleEyesIcon
                                      isOpen={!shape.props.isHiddenLayerBlur}
                                      onClick={() => handleToggleLayerBlurForShape(shape.id)}
                                    />
                                    {selectedShapes.length === 1 && (
                                      <MinusIcon
                                        className="h-5 w-5 cursor-pointer"
                                        onClick={() => handleRemoveLayerBlurForShape(shape.id)}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default EffectSidebar;

const BlurDotsIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 14.5C2.86667 14.5 2.75 14.45 2.65 14.35C2.55 14.25 2.5 14.1333 2.5 14C2.5 13.8667 2.55 13.75 2.65 13.65C2.75 13.55 2.86667 13.5 3 13.5C3.13333 13.5 3.25 13.55 3.35 13.65C3.45 13.75 3.5 13.8667 3.5 14C3.5 14.1333 3.45 14.25 3.35 14.35C3.25 14.45 3.13333 14.5 3 14.5ZM3 10.5C2.86667 10.5 2.75 10.45 2.65 10.35C2.55 10.25 2.5 10.1333 2.5 10C2.5 9.86667 2.55 9.75 2.65 9.65C2.75 9.55 2.86667 9.5 3 9.5C3.13333 9.5 3.25 9.55 3.35 9.65C3.45 9.75 3.5 9.86667 3.5 10C3.5 10.1333 3.45 10.25 3.35 10.35C3.25 10.45 3.13333 10.5 3 10.5ZM6 19C5.71667 19 5.47917 18.9042 5.2875 18.7125C5.09583 18.5208 5 18.2833 5 18C5 17.7167 5.09583 17.4792 5.2875 17.2875C5.47917 17.0958 5.71667 17 6 17C6.28333 17 6.52083 17.0958 6.7125 17.2875C6.90417 17.4792 7 17.7167 7 18C7 18.2833 6.90417 18.5208 6.7125 18.7125C6.52083 18.9042 6.28333 19 6 19ZM6 15C5.71667 15 5.47917 14.9042 5.2875 14.7125C5.09583 14.5208 5 14.2833 5 14C5 13.7167 5.09583 13.4792 5.2875 13.2875C5.47917 13.0958 5.71667 13 6 13C6.28333 13 6.52083 13.0958 6.7125 13.2875C6.90417 13.4792 7 13.7167 7 14C7 14.2833 6.90417 14.5208 6.7125 14.7125C6.52083 14.9042 6.28333 15 6 15ZM6 11C5.71667 11 5.47917 10.9042 5.2875 10.7125C5.09583 10.5208 5 10.2833 5 10C5 9.71667 5.09583 9.47917 5.2875 9.2875C5.47917 9.09583 5.71667 9 6 9C6.28333 9 6.52083 9.09583 6.7125 9.2875C6.90417 9.47917 7 9.71667 7 10C7 10.2833 6.90417 10.5208 6.7125 10.7125C6.52083 10.9042 6.28333 11 6 11ZM6 7C5.71667 7 5.47917 6.90417 5.2875 6.7125C5.09583 6.52083 5 6.28333 5 6C5 5.71667 5.09583 5.47917 5.2875 5.2875C5.47917 5.09583 5.71667 5 6 5C6.28333 5 6.52083 5.09583 6.7125 5.2875C6.90417 5.47917 7 5.71667 7 6C7 6.28333 6.90417 6.52083 6.7125 6.7125C6.52083 6.90417 6.28333 7 6 7ZM10 15.5C9.58333 15.5 9.22917 15.3542 8.9375 15.0625C8.64583 14.7708 8.5 14.4167 8.5 14C8.5 13.5833 8.64583 13.2292 8.9375 12.9375C9.22917 12.6458 9.58333 12.5 10 12.5C10.4167 12.5 10.7708 12.6458 11.0625 12.9375C11.3542 13.2292 11.5 13.5833 11.5 14C11.5 14.4167 11.3542 14.7708 11.0625 15.0625C10.7708 15.3542 10.4167 15.5 10 15.5ZM10 11.5C9.58333 11.5 9.22917 11.3542 8.9375 11.0625C8.64583 10.7708 8.5 10.4167 8.5 10C8.5 9.58333 8.64583 9.22917 8.9375 8.9375C9.22917 8.64583 9.58333 8.5 10 8.5C10.4167 8.5 10.7708 8.64583 11.0625 8.9375C11.3542 9.22917 11.5 9.58333 11.5 10C11.5 10.4167 11.3542 10.7708 11.0625 11.0625C10.7708 11.3542 10.4167 11.5 10 11.5ZM10 19C9.71667 19 9.47917 18.9042 9.2875 18.7125C9.09583 18.5208 9 18.2833 9 18C9 17.7167 9.09583 17.4792 9.2875 17.2875C9.47917 17.0958 9.71667 17 10 17C10.2833 17 10.5208 17.0958 10.7125 17.2875C10.9042 17.4792 11 17.7167 11 18C11 18.2833 10.9042 18.5208 10.7125 18.7125C10.5208 18.9042 10.2833 19 10 19ZM10 7C9.71667 7 9.47917 6.90417 9.2875 6.7125C9.09583 6.52083 9 6.28333 9 6C9 5.71667 9.09583 5.47917 9.2875 5.2875C9.47917 5.09583 9.71667 5 10 5C10.2833 5 10.5208 5.09583 10.7125 5.2875C10.9042 5.47917 11 5.71667 11 6C11 6.28333 10.9042 6.52083 10.7125 6.7125C10.5208 6.90417 10.2833 7 10 7ZM10 21.5C9.86667 21.5 9.75 21.45 9.65 21.35C9.55 21.25 9.5 21.1333 9.5 21C9.5 20.8667 9.55 20.75 9.65 20.65C9.75 20.55 9.86667 20.5 10 20.5C10.1333 20.5 10.25 20.55 10.35 20.65C10.45 20.75 10.5 20.8667 10.5 21C10.5 21.1333 10.45 21.25 10.35 21.35C10.25 21.45 10.1333 21.5 10 21.5ZM10 3.5C9.86667 3.5 9.75 3.45 9.65 3.35C9.55 3.25 9.5 3.13333 9.5 3C9.5 2.86667 9.55 2.75 9.65 2.65C9.75 2.55 9.86667 2.5 10 2.5C10.1333 2.5 10.25 2.55 10.35 2.65C10.45 2.75 10.5 2.86667 10.5 3C10.5 3.13333 10.45 3.25 10.35 3.35C10.25 3.45 10.1333 3.5 10 3.5ZM14 15.5C13.5833 15.5 13.2292 15.3542 12.9375 15.0625C12.6458 14.7708 12.5 14.4167 12.5 14C12.5 13.5833 12.6458 13.2292 12.9375 12.9375C13.2292 12.6458 13.5833 12.5 14 12.5C14.4167 12.5 14.7708 12.6458 15.0625 12.9375C15.3542 13.2292 15.5 13.5833 15.5 14C15.5 14.4167 15.3542 14.7708 15.0625 15.0625C14.7708 15.3542 14.4167 15.5 14 15.5ZM14 11.5C13.5833 11.5 13.2292 11.3542 12.9375 11.0625C12.6458 10.7708 12.5 10.4167 12.5 10C12.5 9.58333 12.6458 9.22917 12.9375 8.9375C13.2292 8.64583 13.5833 8.5 14 8.5C14.4167 8.5 14.7708 8.64583 15.0625 8.9375C15.3542 9.22917 15.5 9.58333 15.5 10C15.5 10.4167 15.3542 10.7708 15.0625 11.0625C14.7708 11.3542 14.4167 11.5 14 11.5ZM14 19C13.7167 19 13.4792 18.9042 13.2875 18.7125C13.0958 18.5208 13 18.2833 13 18C13 17.7167 13.0958 17.4792 13.2875 17.2875C13.4792 17.0958 13.7167 17 14 17C14.2833 17 14.5208 17.0958 14.7125 17.2875C14.9042 17.4792 15 17.7167 15 18C15 18.2833 14.9042 18.5208 14.7125 18.7125C14.5208 18.9042 14.2833 19 14 19ZM14 7C13.7167 7 13.4792 6.90417 13.2875 6.7125C13.0958 6.52083 13 6.28333 13 6C13 5.71667 13.0958 5.47917 13.2875 5.2875C13.4792 5.09583 13.7167 5 14 5C14.2833 5 14.5208 5.09583 14.7125 5.2875C14.9042 5.47917 15 5.71667 15 6C15 6.28333 14.9042 6.52083 14.7125 6.7125C14.5208 6.90417 14.2833 7 14 7ZM14 21.5C13.8667 21.5 13.75 21.45 13.65 21.35C13.55 21.25 13.5 21.1333 13.5 21C13.5 20.8667 13.55 20.75 13.65 20.65C13.75 20.55 13.8667 20.5 14 20.5C14.1333 20.5 14.25 20.55 14.35 20.65C14.45 20.75 14.5 20.8667 14.5 21C14.5 21.1333 14.45 21.25 14.35 21.35C14.25 21.45 14.1333 21.5 14 21.5ZM14 3.5C13.8667 3.5 13.75 3.45 13.65 3.35C13.55 3.25 13.5 3.13333 13.5 3C13.5 2.86667 13.55 2.75 13.65 2.65C13.75 2.55 13.8667 2.5 14 2.5C14.1333 2.5 14.25 2.55 14.35 2.65C14.45 2.75 14.5 2.86667 14.5 3C14.5 3.13333 14.45 3.25 14.35 3.35C14.25 3.45 14.1333 3.5 14 3.5ZM18 19C17.7167 19 17.4792 18.9042 17.2875 18.7125C17.0958 18.5208 17 18.2833 17 18C17 17.7167 17.0958 17.4792 17.2875 17.2875C17.4792 17.0958 17.7167 17 18 17C18.2833 17 18.5208 17.0958 18.7125 17.2875C18.9042 17.4792 19 17.7167 19 18C19 18.2833 18.9042 18.5208 18.7125 18.7125C18.5208 18.9042 18.2833 19 18 19ZM18 15C17.7167 15 17.4792 14.9042 17.2875 14.7125C17.0958 14.5208 17 14.2833 17 14C17 13.7167 17.0958 13.4792 17.2875 13.2875C17.4792 13.0958 17.7167 13 18 13C18.2833 13 18.5208 13.0958 18.7125 13.2875C18.9042 13.4792 19 13.7167 19 14C19 14.2833 18.9042 14.5208 18.7125 14.7125C18.5208 14.9042 18.2833 15 18 15ZM18 11C17.7167 11 17.4792 10.9042 17.2875 10.7125C17.0958 10.5208 17 10.2833 17 10C17 9.71667 17.0958 9.47917 17.2875 9.2875C17.4792 9.09583 17.7167 9 18 9C18.2833 9 18.5208 9.09583 18.7125 9.2875C18.9042 9.47917 19 9.71667 19 10C19 10.2833 18.9042 10.5208 18.7125 10.7125C18.5208 10.9042 18.2833 11 18 11ZM18 7C17.7167 7 17.4792 6.90417 17.2875 6.7125C17.0958 6.52083 17 6.28333 17 6C17 5.71667 17.0958 5.47917 17.2875 5.2875C17.4792 5.09583 17.7167 5 18 5C18.2833 5 18.5208 5.09583 18.7125 5.2875C18.9042 5.47917 19 5.71667 19 6C19 6.28333 18.9042 6.52083 18.7125 6.7125C18.5208 6.90417 18.2833 7 18 7ZM21 14.5C20.8667 14.5 20.75 14.45 20.65 14.35C20.55 14.25 20.5 14.1333 20.5 14C20.5 13.8667 20.55 13.75 20.65 13.65C20.75 13.55 20.8667 13.5 21 13.5C21.1333 13.5 21.25 13.55 21.35 13.65C21.45 13.75 21.5 13.8667 21.5 14C21.5 14.1333 21.45 14.25 21.35 14.35C21.25 14.45 21.1333 14.5 21 14.5ZM21 10.5C20.8667 10.5 20.75 10.45 20.65 10.35C20.55 10.25 20.5 10.1333 20.5 10C20.5 9.86667 20.55 9.75 20.65 9.65C20.75 9.55 20.8667 9.5 21 9.5C21.1333 9.5 21.25 9.55 21.35 9.65C21.45 9.75 21.5 9.86667 21.5 10C21.5 10.1333 21.45 10.25 21.35 10.35C21.25 10.45 21.1333 10.5 21 10.5Z"
      fill="#1A2547"
    />
  </svg>
);

const LayerBlurRow: React.FC<{
  shape: any;
  onChange: (value: number) => void;
}> = ({ shape, onChange }) => {
  const value = shape.props?.filterBlur ?? 0;
  const valueRef = useRef(value);
  valueRef.current = value;
  const { onMouseDown } = useDragToAdjust(() => valueRef.current, onChange, { min: 0, max: 50, step: 1 });

  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  const commit = (raw: string) => {
    if (raw.trim() === "") {
      setText(String(value));
      return;
    }
    const n = parseFloat(raw);
    if (isNaN(n)) {
      setText(String(value));
      return;
    }
    onChange(Math.max(0, Math.min(50, n)));
  };

  return (
    <div
      className="flex items-center rounded-[4px] px-2 py-1 bg-white"
      style={{ border: "1px solid #E2E2FC", fontFamily: "Inter, sans-serif" }}
    >
      <span
        onMouseDown={onMouseDown}
        title="Drag to adjust blur"
        style={{ cursor: "ew-resize", display: "inline-flex" }}
      >
        <BlurDotsIcon />
      </span>
      <span
        style={{
          width: 25.919,
          color: "#2E3860",
          fontFamily: "Inter, sans-serif",
          fontSize: 10,
          fontWeight: 400,
          lineHeight: "16px",
          marginLeft: 4,
          fontFeatureSettings: "'liga' off, 'clig' off",
        }}
      >
        Blur
      </span>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={focused ? text : String(value)}
        onChange={(e) => {
          setText(e.target.value);
          if (e.target.value === "") return;
          const n = parseFloat(e.target.value);
          if (!isNaN(n)) onChange(Math.max(0, Math.min(50, n)));
        }}
        onFocus={() => {
          setFocused(true);
          setText(String(value));
        }}
        onBlur={() => {
          commit(text);
          setFocused(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commit(text);
            inputRef.current?.blur();
          } else if (e.key === "Escape") {
            setText(String(value));
            inputRef.current?.blur();
          }
        }}
        className="bg-transparent outline-none no-arrows text-right ml-auto"
        style={{
          width: 28,
          height: 16,
          color: "#2E3860",
          fontFamily: "Inter, sans-serif",
          fontSize: 10,
          fontWeight: 400,
          lineHeight: "16px",
          fontFeatureSettings: "'liga' off, 'clig' off",
        }}
      />
    </div>
  );
};

interface ToggleIconProps {
  isOpen: boolean;
  onClick: () => void;
}

export const ToggleIcon: React.FC<ToggleIconProps> = ({ isOpen, onClick }) => {
  return isOpen ? (
    <MinusIcon className="h-5 w-5 cursor-pointer" onClick={onClick} />
  ) : (
    <PlusIcon className="h-5 w-5 cursor-pointer" onClick={onClick} />
  );
};

interface TitleProps {
  name: string;
  /**
   * If provided, the label itself becomes clickable and fires this
   * handler — same affordance as the trailing `+` icon, so users can
   * hit either the label or the icon to add a new Fill / Stroke /
   * Effect. Cursor and select-none switch on so it reads as a
   * button. When omitted, the title renders as plain inert text.
   */
  onClick?: () => void;
}

export const Title: React.FC<TitleProps> = ({ name, onClick }) => {
  return (
    <p
      role={onClick ? "button" : undefined}
      onClick={onClick}
      className={`text-[#2E3860] font-semibold !text-[14px] leading-6${
        onClick ? " cursor-pointer select-none hover:text-[#0C172C]" : ""
      }`}
    >
      {name}
    </p>
  );
};
